import { Icon } from '@hologram-dimension/icon';
import type { IconNames } from '@hologram-dimension/icons';
import clsx from 'clsx/lite';
import {
  type FC,
  type KeyboardEventHandler,
  type MouseEventHandler,
  useCallback,
  useMemo,
} from 'react';
import type { SortDirection } from 'state/SimInventory/SimInventoryStore';
import useSimInventoryStore from 'state/SimInventory/useSimInventoryStore';
import useSimInventoryStoredSorts from 'state/SimInventory/useSimInventoryStoredSorts';
import type { SimInventorySegmentName, SimKey } from 'types/common';
import useAnalytics from 'state/contexts/Analytics/useAnalytics';
import useColumnsForSegment from './ColumnDefinition/useColumnDefsForSegment';
import styles from './SimInventoryTableHeaderCell.module.css';

interface SimInventoryTableHeaderCellProps {
  children: string;
  segmentName: SimInventorySegmentName;
  columnValue: SimKey;
}

// TODO: Central location for meta columns. Perhaps ColumnDefs or related?
const configColummns = ['testConfig', 'configuration'];
const allMetaColumns = [...configColummns];

const metaColumnToSortableKeys = (columnValue: any): Array<SimKey> => {
  if (!allMetaColumns.includes(columnValue)) {
    return [columnValue as SimKey];
  }

  if (configColummns.includes(columnValue)) {
    return ['preflightDuration', 'preflightExitBehavior'];
  }

  return [columnValue];
};

const SimInventoryTableHeaderCell: FC<SimInventoryTableHeaderCellProps> = ({
  children,
  segmentName,
  columnValue,
}) => {
  const { columnDefs } = useColumnsForSegment({ segmentName });
  const columnDef = columnDefs.get(columnValue);
  const sorts = useSimInventoryStoredSorts();
  const sortableColumnValues = useMemo(() => metaColumnToSortableKeys(columnValue), [columnValue]);
  const allSorts = sorts.filter((value) => sortableColumnValues.includes(value.property));
  const { sendAnalyticsEvent } = useAnalytics();

  // TODO: Right now the meta-columns are tied to their first field, perhaps we should revisit that?
  const sort = allSorts[0];
  const sorted = sort?.ordering;

  const setSort = useSimInventoryStore((state) => state.setSort);

  const sortable = true;

  let sortedIcon: IconNames;

  if (sortable) {
    switch (sorted) {
      case 'asc':
        sortedIcon = 'ArrowTriangleNorth';
        break;

      case 'desc':
        sortedIcon = 'ArrowTriangleSouth';
        break;

      default:
        sortedIcon = 'ArrowTriangleNorthSouth';
        break;
    }
  }

  const toggleSort = useCallback(() => {
    let nextSortDirection: SortDirection;

    // TODO: rethink how to configure sort toggling
    switch (sorted) {
      case 'asc':
        nextSortDirection = columnDef?.rightAligned ? undefined : 'desc';
        break;
      case 'desc':
        nextSortDirection = columnDef?.rightAligned ? 'asc' : undefined;
        break;

      default:
        nextSortDirection = columnDef?.rightAligned ? 'desc' : 'asc';
        break;
    }

    let nullsFirst: boolean | undefined;
    switch (nextSortDirection) {
      case 'asc':
        nullsFirst = !columnDef?.nullsHigher;
        break;

      case 'desc':
        nullsFirst = !!columnDef?.nullsHigher;
        break;

      default:
        nullsFirst = undefined;
        break;
    }

    // TODO: apply all sorted columns from array here, or set multiple?
    const properties = metaColumnToSortableKeys(columnValue);

    if (nextSortDirection === undefined) {
      setSort(undefined);
    } else {
      setSort(
        properties.map((property) => ({
          property,
          ordering: nextSortDirection,
          nullsFirst,
        }))
      );
    }
    if (sendAnalyticsEvent)
      sendAnalyticsEvent(`Sort - ${children} ${nextSortDirection ?? 'default'}`);
  }, [columnValue, columnDef, setSort, sorted, children, sendAnalyticsEvent]);

  const handleClick: MouseEventHandler = useCallback(() => {
    toggleSort();
  }, [toggleSort]);

  const handleKey: KeyboardEventHandler = useCallback(
    (e) => {
      if (!(e.key === ' ' || e.key === 'Enter')) {
        return;
      }
      toggleSort();
    },
    [toggleSort]
  );

  if (!columnDef) {
    return null;
  }

  return (
    <div
      className={clsx(styles.wrapper, sortable && styles.sortable, sorted && styles.sorted)}
      onClick={sortable ? handleClick : undefined}
      onKeyUp={sortable ? handleKey : undefined}
      tabIndex={0}
      role="button"
      aria-label={`${children} column`}
      id={`header-cell-${columnDef.value}`}
    >
      {sortable && columnDef.rightAligned && (
        <Icon size="small" name={sortedIcon!} className={styles.sortIcon} color="currentColor" />
      )}
      <div className={styles.headerText}>{children}</div>
      {sortable && !columnDef.rightAligned && (
        <Icon size="small" name={sortedIcon!} className={styles.sortIcon} color="currentColor" />
      )}
    </div>
  );
};

export default SimInventoryTableHeaderCell;
